import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  ExternalLink,
  Target,
  CheckCircle2,
  TrendingUp,
  Layers,
  Users,
  Clock,
  Sparkles,
  ArrowRight,
  Shield,
  Loader2,
  Star,
} from "lucide-react";
import { projectsService } from "@/api/projectsService";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Reveal } from "@/components/site/SiteLayout";
import { cn } from "@/lib/utils";

export default function ProjectDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;

    async function fetchProject() {
      try {
        setLoading(true);
        setError(null);
        const data = await projectsService.getProjectBySlug(slug);

        if (!mounted) return;

        if (!data || data.deleted_at || !data.is_published) {
          setError("Case study not found or currently in draft.");
          setProject(null);
        } else {
          setProject(data);
          // Set dynamic SEO tags
          const pageTitle = data.meta_title || `${data.title} Case Study | Shiveda IT Services`;
          document.title = pageTitle;

          const metaDesc = document.querySelector('meta[name="description"]');
          if (metaDesc) {
            metaDesc.setAttribute(
              "content",
              data.meta_description || data.short_summary || data.challenge_description || ""
            );
          }
        }
      } catch (err) {
        if (mounted) {
          console.error("[ProjectDetail] Error fetching project:", err);
          setError("Failed to load project details.");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    fetchProject();

    return () => {
      mounted = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="w-full min-h-[60vh] px-5 py-24 flex flex-col items-center justify-center gap-4 text-white">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <div className="text-xs text-white/50 tracking-wider font-mono uppercase">
          Loading Case Study...
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="w-full min-h-[60vh] px-5 py-24 flex flex-col items-center justify-center text-center text-white">
        <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-white/40 mb-4">
          <Target className="w-8 h-8" />
        </div>
        <h1 className="text-2xl font-bold text-white">Case Study Not Found</h1>
        <p className="text-sm text-white/60 mt-2 max-w-md">
          {error || "The requested project case study could not be located or has not been published yet."}
        </p>
        <Button
          onClick={() => navigate("/projects")}
          className="mt-6 rounded-xl bg-primary text-black hover:bg-primary/90 text-xs font-semibold h-10 px-5 gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to All Projects</span>
        </Button>
      </div>
    );
  }

  // Parse metrics list safely
  const metrics = Array.isArray(project.metrics)
    ? project.metrics
    : typeof project.metrics === "object"
    ? Object.entries(project.metrics).map(([k, v]) => ({
        label: k,
        value: typeof v === "object" ? v.value : v,
        desc: typeof v === "object" ? v.desc : "",
      }))
    : [];

  // Filter media
  const galleryMedia = (project.media || []).filter((m) => m.media_type === "gallery");
  const heroImage = project.heroImage || project.thumbnail || null;

  return (
    <div className="w-full px-5 py-12 xl:px-12 2xl:px-16 max-w-6xl mx-auto space-y-12">
      {/* Back Link */}
      <div>
        <Link
          to="/projects"
          className="inline-flex items-center gap-2 text-xs font-medium text-white/60 hover:text-primary transition-colors group"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
          <span>Back to All Projects</span>
        </Link>
      </div>

      {/* Hero Header */}
      <Reveal delay={0}>
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2.5">
            <Badge className="bg-primary/15 text-primary border-primary/30 px-3 py-1 text-xs font-semibold">
              {project.category}
            </Badge>

            <Badge
              className={cn(
                "text-xs px-3 py-1 font-semibold",
                project.status === "Completed"
                  ? "bg-green-500/15 text-green-400 border-green-500/25"
                  : "bg-yellow-500/15 text-yellow-400 border-yellow-500/25"
              )}
            >
              {project.status === "Completed" ? (
                <CheckCircle2 className="w-3.5 h-3.5 mr-1 inline" />
              ) : (
                <Clock className="w-3.5 h-3.5 mr-1 inline" />
              )}
              <span>{project.status}</span>
            </Badge>

            {project.client && (
              <span className="text-xs text-white/50 flex items-center gap-1.5 ml-1">
                <Users className="w-3.5 h-3.5" />
                <span>Client: {project.client}</span>
              </span>
            )}

            {project.team_size && (
              <span className="text-xs text-white/50 flex items-center gap-1.5 ml-1">
                <Shield className="w-3.5 h-3.5 text-primary/70" />
                <span>Team: {project.team_size}</span>
              </span>
            )}
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white leading-tight">
            {project.title}
          </h1>

          {project.short_summary && (
            <p className="text-base sm:text-lg text-white/70 leading-relaxed max-w-3xl">
              {project.short_summary}
            </p>
          )}

          {/* Action Links */}
          <div className="flex flex-wrap items-center gap-3 pt-2">
            {project.live_url && (
              <Button
                onClick={() => window.open(project.live_url, "_blank")}
                className="rounded-xl bg-primary text-black hover:bg-primary/90 font-semibold text-xs h-11 px-6 gap-2 shadow-[0_0_20px_rgba(0,194,255,0.3)]"
              >
                <span>Visit Live Platform</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </Button>
            )}

            <Button
              onClick={() => navigate("/contact#form")}
              variant="outline"
              className="rounded-xl border-white/15 bg-white/[0.04] text-white hover:bg-white/[0.08] text-xs font-semibold h-11 px-6 gap-2"
            >
              <span>Build Similar Project</span>
              <ArrowRight className="w-3.5 h-3.5 text-primary" />
            </Button>
          </div>
        </div>
      </Reveal>

      {/* Hero Visual Asset (if provided in project-media) */}
      {heroImage && (
        <Reveal delay={80}>
          <div className="rounded-3xl border border-white/10 overflow-hidden bg-white/[0.02] shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
            <img
              src={heroImage}
              alt={project.title}
              className="w-full max-h-[480px] object-cover"
            />
          </div>
        </Reveal>
      )}

      {/* Core Case Study Sections */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* The Challenge */}
        {project.challenge_description && (
          <Reveal delay={100}>
            <div className="rounded-2xl border border-orange-500/20 bg-gradient-to-br from-orange-500/5 to-transparent p-6 h-full flex flex-col">
              <div className="flex items-center gap-2 text-orange-400 font-bold text-xs uppercase tracking-wider mb-4">
                <Target className="w-4 h-4" />
                <span>The Challenge</span>
              </div>
              <p className="text-sm sm:text-base leading-relaxed text-white/80 whitespace-pre-line">
                {project.challenge_description}
              </p>
            </div>
          </Reveal>
        )}

        {/* Our Solution */}
        {project.solution_description && (
          <Reveal delay={140}>
            <div className="rounded-2xl border border-green-500/20 bg-gradient-to-br from-green-500/5 to-transparent p-6 h-full flex flex-col">
              <div className="flex items-center gap-2 text-green-400 font-bold text-xs uppercase tracking-wider mb-4">
                <CheckCircle2 className="w-4 h-4" />
                <span>Our Engineering Solution</span>
              </div>
              <p className="text-sm sm:text-base leading-relaxed text-white/80 whitespace-pre-line">
                {project.solution_description}
              </p>
            </div>
          </Reveal>
        )}

        {/* Business Impact */}
        {project.outcome_description && (
          <Reveal delay={180}>
            <div className="rounded-2xl border border-blue-500/20 bg-gradient-to-br from-blue-500/5 to-transparent p-6 h-full flex flex-col">
              <div className="flex items-center gap-2 text-blue-400 font-bold text-xs uppercase tracking-wider mb-4">
                <TrendingUp className="w-4 h-4" />
                <span>Business Impact & Results</span>
              </div>
              <p className="text-sm sm:text-base leading-relaxed text-white/80 whitespace-pre-line">
                {project.outcome_description}
              </p>
            </div>
          </Reveal>
        )}
      </div>

      {/* Dynamic Measurable Results Grid (No Hardcoded Stats!) */}
      {metrics.length > 0 && (
        <Reveal delay={200}>
          <div className="rounded-3xl border border-primary/25 bg-primary/[0.03] p-8 space-y-6">
            <div className="flex items-center gap-2 text-primary font-bold text-xs uppercase tracking-wider">
              <Sparkles className="w-4 h-4" />
              <span>Verified Results & Measured Performance</span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {metrics.map((m, idx) => (
                <div
                  key={m.id || idx}
                  className="rounded-2xl border border-white/10 bg-black/40 p-5 backdrop-blur-md"
                >
                  <div className="text-3xl sm:text-4xl font-bold text-primary tracking-tight">
                    {m.value}
                  </div>
                  <div className="text-xs font-semibold text-white mt-1.5">{m.label}</div>
                  {m.desc && <div className="text-[11px] text-white/50 mt-0.5">{m.desc}</div>}
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      )}

      {/* Technology Stack Showcase */}
      {project.stack && project.stack.length > 0 && (
        <Reveal delay={220}>
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 space-y-4">
            <div className="flex items-center gap-2 text-white font-bold text-xs uppercase tracking-wider text-white/60">
              <Layers className="w-4 h-4 text-primary" />
              <span>Engineered With</span>
            </div>

            <div className="flex flex-wrap gap-2.5">
              {project.stack.map((tech) => (
                <Badge
                  key={tech}
                  className="px-3.5 py-1.5 rounded-xl border-primary/20 bg-primary/5 text-primary text-xs font-medium hover:bg-primary/10 transition-colors"
                >
                  {tech}
                </Badge>
              ))}
            </div>
          </div>
        </Reveal>
      )}

      {/* Gallery Screenshots (if any) */}
      {galleryMedia.length > 0 && (
        <Reveal delay={240}>
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider text-white/60">
              Interface Gallery
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {galleryMedia.map((m) => (
                <div
                  key={m.id}
                  className="rounded-2xl border border-white/10 overflow-hidden bg-white/[0.02]"
                >
                  <img
                    src={m.publicUrl}
                    alt={m.alt_text || project.title}
                    className="w-full aspect-video object-cover"
                  />
                  {m.caption && (
                    <div className="p-3 text-xs text-white/60 bg-black/60 border-t border-white/5">
                      {m.caption}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      )}

      {/* Verified Client Feedback / Testimonial (if present in database) */}
      {project.testimonial_quote && (
        <Reveal delay={250}>
          <div className="rounded-3xl border border-primary/20 bg-gradient-to-br from-primary/5 via-transparent to-transparent p-8 sm:p-10 space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5 text-primary font-bold text-xs uppercase tracking-wider">
                <Users className="w-4 h-4" />
                <span>Client Feedback & Endorsement</span>
              </div>
              <div className="flex items-center gap-1 text-amber-400">
                {Array.from({ length: project.testimonial_rating || 5 }).map((_, starIdx) => (
                  <Star key={starIdx} className="h-4 w-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
            </div>

            <div className="relative">
              <p className="text-base sm:text-lg leading-relaxed text-white/90 italic">
                "{project.testimonial_quote}"
              </p>
              <div className="mt-5 flex items-center gap-3">
                <div className="h-11 w-11 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/30 flex items-center justify-center">
                  <span className="text-sm font-bold text-primary">
                    {(project.testimonial_author || project.client || "C").charAt(0)}
                  </span>
                </div>
                <div>
                  <div className="text-sm font-semibold text-white">
                    {project.testimonial_author || project.client}
                  </div>
                  <div className="text-xs text-white/60">
                    {project.testimonial_role || `${project.category} Client`}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      )}

      <Separator className="bg-white/10 my-8" />

      {/* Bottom CTA Box */}
      <Reveal delay={260}>
        <div className="rounded-3xl border border-primary/20 bg-gradient-to-br from-primary/10 via-transparent to-transparent p-8 sm:p-12 text-center space-y-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-white">
            Ready to Engineer Something Extraordinary?
          </h2>
          <p className="text-sm text-white/60 max-w-xl mx-auto leading-relaxed">
            Partner with Shiveda IT Services to design, build, and deploy reliable digital products tailored to your business vision.
          </p>
          <div className="pt-2">
            <Button
              onClick={() => navigate("/contact#form")}
              className="h-12 px-8 rounded-xl bg-primary text-black hover:bg-primary/90 font-semibold text-xs gap-2 shadow-[0_0_24px_rgba(0,194,255,0.35)]"
            >
              <span>Schedule Technical Consultation</span>
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Reveal>
    </div>
  );
}
